import React, {
    Fragment,
    Suspense,
    lazy,
    forwardRef,
    useRef,
    useState,
    useEffect,
    useContext,
    useCallback
} from 'react';
import PropTypes from 'prop-types';

// 🔖 reactrouter.com/web/api/Route/path-string-string
// 🔖 github.com/pillarjs/path-to-regexp/
import {
    BrowserRouter,
    HashRouter,
    Redirect,
    Route,
    Link,
    Switch,
    useHistory,
    useLocation
} from 'react-router-dom';

import { start as startIntercom } from '@sbu/utils.intercom';
import { ApolloProvider } from '@sbu/contexts.apollo';
import { ToastsContext } from '@sbu/contexts.toasts';
import { ReactQueryProvider } from '@sbu/contexts.reactquery';
import { useTheme } from '@sumologic/yama-ds-components/hooks';

import { checkFeatureFlag } from '~/modules/helpers';
import { defaultValuesKeys } from '~/modules/defaultValues';
import { returnSpecificPath } from '~/modules/manageRouting';
import { generateMockedCsoarUserPrivileges } from '~/modules/EXTERNAL_MOCK_USER_PRIVILEGE';

import User from '~/models/User';

import Loader from '~/components/Loader/Loader';
import Topbar from '~/components/Topbar/Topbar';

import AppContext from '~/containers/App/AppContext';
import CanContext from '~/containers/Can/CanContext';
import ContextualMenu from '~/containers/ContextualMenu/ContextualMenu';
import PreferencesContext from '~/containers/Preferences/PreferencesContext';
import PreferencesManager from '~/containers/Preferences/PreferencesManager';

import PageNotFound from '~/pages/PageNotFound/PageNotFound';
import AccessDenied from '~/pages/AccessDenied/AccessDenied';
import PlaybookExecuteDetailsPage from '~/pages/PlaybookExecuteDetails/PlaybookExecuteDetailsPage';
import {
    dashboardSection,
    secopsSection
} from '~/pages/Home/HomeDashboard/Elements/Cards/CardSidebarLeftTop';
import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';
import useIntercomInitialize from '~/containers/AppRouter/useIntercomInitialize';

//----------------------------------------------------------------------------------------------------------------------

const oldUI = !SETTINGS.NEWUI;

// Lazy load Nav component only for new UI
const LazyNav = lazy(() =>
    import(
        /* webpackChunkName: "views-nav" */
        '@sbu/views.nav'
    ).then(module => ({ default: module.Nav }))
);

// 𝗼𝗹𝗱 𝘶𝘪 𝘩𝘢𝘷𝘦 pipe '|', 𝗻𝗲𝘄 𝘶𝘪 𝘩𝘢𝘷𝘦 𝘴𝘭𝘢𝘴𝘩 '/'
const incidentSectionRE = oldUI ? /incident\|\d+/ : /cases\/\d+/;

const printPageUrlRE = oldUI ? /print\|.+/ : /csoar\/ui\/print\/.+/;

//----------------------------------------------------------------------------------------------------------------------# 𝘱𝘦𝘳𝘮𝘪𝘴𝘴𝘪𝘰𝘯𝘴

// prettier-ignore
const permissions = {
    report:'report_access',
    entities: 'objects_access',
    incidentCreation: 'incident_edit',
    playbookExecutes: 'observability_access',
    triage: [
        'triage_read_only',
        'triage_access_all',
        'triage_access'
    ],
    incident: [
        'incident_read_only',
        'incident_access_all',
        'incident_access'
    ],
    incidentTemplate: [
        'incident_templates_access',
        'incident_templates_management'
    ],
    integrations: [
        'integrations_access',
        'integrations_management'
    ],
    automationRules: [
        'automation_rules_access',
        'automation_rules_management'
    ],
    automationSettings: [ // todo: split? check old management
        'playbook_access',
        'marketplace_access',
        'automation_rules_access',
        'automation_rules_management',
        'incident_templates_access',
        'incident_templates_management',
        'integrations_access',
        'integrations_management',
        'bridge_monitoring_access'
    ]
};

//----------------------------------------------------------------------------------------------------------------------# 𝘊𝘰𝘮𝘱𝘰𝘯𝘦𝘯𝘵𝘴 𝗱𝗲𝘃

const UserPreferenceReset = lazy(() =>
    import(
        /* webpackChunkName: "UserPreferenceReset" */
        '../UserPreferenceReset/UserPreferenceReset'
    )
);

const Sandbox = lazy(() =>
    import(
        /* webpackChunkName: "Sandbox" */
        '~/pages/Sandbox/Sandbox'
    )
);

//----------------------------------------------------------------------------------------------------------------------# 𝘊𝘰𝘮𝘱𝘰𝘯𝘦𝘯𝘵𝘴

const Report = lazy(() =>
    import(
        /* webpackChunkName: "Report" */
        '~/pages/Report/Report'
    )
);

const ReportRendering = lazy(() =>
    import(
        /* webpackChunkName: "ReportPrinter" */
        '~/pages/Report/ReportPrinter/ReportPrinter'
    )
);

const Support = lazy(() =>
    import(
        /* webpackChunkName: "Support" */
        '~/pages/Support/Support'
    )
);

const GeneralSettings = lazy(() =>
    import(
        /* webpackChunkName: "GeneralSettings" */
        '~/pages/GeneralSettings/GeneralSettings'
    )
);

const AutomationSettings = lazy(() =>
    import(
        /* webpackChunkName: "AutomationSettings" */
        '~/pages/AutomationSettings/AutomationSettings'
    )
);

const EntitiesServer = lazy(() =>
    import(
        /* webpackChunkName: "EntitiesServer" */
        '~/pages/Entities/EntitiesServer'
    )
);

const IncidentList = lazy(() =>
    import(
        /* webpackChunkName: "IncidentList" */
        '~/pages/IncidentList/IncidentListServer'
    )
);

const IncidentCreation = lazy(() =>
    import(
        /* webpackChunkName: "IncidentCreation" */
        '~/pages/IncidentCreation/IncidentCreation'
    )
);

const IncidentDetails = lazy(() =>
    import(
        /* webpackChunkName: "IncidentDetails" */
        '~/pages/IncidentDetails/IncidentDetails'
    )
);

const TriageListServer = lazy(() =>
    import(
        /* webpackChunkName: "TriageListServer" */
        '~/pages/Triage/TriageList/TriageListServer'
    )
);

const TriageDetails = lazy(() =>
    import(
        /* webpackChunkName: "TriageDetails" */
        '~/pages/Triage/TriageDetails/TriageDetails'
    )
);

const PlaybookExecute = lazy(() =>
    import(
        /* webpackChunkName: "PlaybookExecute" */
        '~/pages/PlaybookExecute/PlaybookExecute'
    )
);

const Training = lazy(() =>
    import(
        /* webpackChunkName: "Training" */
        '~/pages/Training/Training'
    )
);

const UserChoice = lazy(() =>
    import(
        /* webpackChunkName: "UserChoice" */
        '~/pages/UserChoice/UserChoice'
    )
);

//----------------------------------------------------------------------------------------------------------------------# 𝘊𝘰𝘮𝘱𝘰𝘯𝘦𝘯𝘵𝘴 𝗱𝗮𝘀𝗵𝗯𝗼𝗮𝗿𝗱

const Home = lazy(() =>
    import(
        /* webpackChunkName: "Home" */
        '~/pages/Home/Home'
    )
);

const Dashboard = lazy(() =>
    import(
        /* webpackChunkName: "Dashboard" */
        '~/pages/Dashboard/Dashboard'
    )
);

const DashboardRendering = lazy(() =>
    import(
        /* webpackChunkName: "DashboardRendering" */
        '~/pages/Dashboard/DashboardPrinter/DashboardPrinter'
    )
);

const RunbookCustomization = React.lazy(() =>
    import(
        /* webpackChunkName: "RunbookCustomization" */
        '~/pages/AutomationSettings/Runbook/RunbookCustomization'
    )
);

const DashboardOrHome = props => {
    const [section, setSection] = useState(null);
    const ref = useRef({
        featureFlagFullCsoar: checkFeatureFlag('full_csoar')
    }).current;
    const { featureFlagFullCsoar } = ref;
    useEffect(() => {
        ref.isMounted = true;
        if (featureFlagFullCsoar) {
            const { modules } = APP_INFO;
            const canDashboardAccess = User.can('dashboard_access');
            if (
                canDashboardAccess &&
                modules.licenseDim &&
                !modules.licenseIms
            ) {
                setSection(dashboardSection);
            } else {
                User.getPreferences('lastVisitedBetweenDashboardAndHome')
                    .then(response => response?.data?.data?.id)
                    .catch(exception => {
                        console.error(exception);
                    })
                    .then(lastVisited => {
                        if (ref.isMounted) {
                            let activeSection = lastVisited ?? secopsSection;
                            if (
                                secopsSection !== activeSection &&
                                'dashboard' === lastVisited &&
                                !canDashboardAccess
                            ) {
                                activeSection = secopsSection;
                            }
                            setSection(activeSection);
                        }
                    });
            }
        }
        return () => {
            ref.isMounted = false;
        };
    }, []);
    if (featureFlagFullCsoar) {
        return section ? (
            secopsSection === section ? (
                <Home
                    homeDashboardActive={section}
                    setHomeDashboardActive={setSection}
                    {...props}
                />
            ) : (
                <Dashboard
                    homeDashboardActive={section}
                    setHomeDashboardActive={setSection}
                    {...props}
                />
            )
        ) : (
            <Loader />
        );
    } else {
        return <RunbookCustomization {...props} />;
    }
};

//----------------------------------------------------------------------------------------------------------------------# 𝘈𝘱𝘱𝘕𝘢𝘷

const menuCategories = [
    'csoar',
    'automationSettings',
    'soarSettingsSection',
    'soarConfigurationsSection'
];

const isMenuItemLink = menuItem => menuItem.hasOwnProperty('path');

const hasParent = ids => ids.some(id => menuCategories.includes(id));

const incidentSectionCls = 'background';

const AppNewNav = props => {
    const preferencesContext = useContext(PreferencesContext);
    const { toggleTheme } = useContext(AppContext);
    const currentTheme = useTheme()?.palette?.mode || 'dark';
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
    const [kansoPrivileges, setKansoPrivileges] = useState(null);
    const intercomInitialized = useIntercomInitialize();

    useEffect(() => {
        if (intercomInitialized) {
            try {
                window.Intercom?.(
                    'onUnreadCountChange',
                    (unreadCount) => {
                        setUnreadNotificationCount( unreadCount);
                    }
                );
            } catch (error) {
                console.error('Failed to get unread count:', error);
            }
        }
    }, [intercomInitialized, setUnreadNotificationCount]);

    useEffect(() => {
        generateMockedCsoarUserPrivileges().then(data => {
            setKansoPrivileges(data);
        });
    }, []);

    const stickyAlertContext = useContext(StickyAlertContext);
    if (props.isPrintPage) {
        return <div className="h-100">{props.children}</div>;
    }

    const location = useLocation();
    const pathname = location?.pathname;

    const history = useHistory();
    const handleNavigateTo = useCallback(
        to => {
            history.push(to);
        },
        [history]
    );

    return (
        <ToastsContext.Provider
            value={{
                add: () => 0,
                addError: message => {
                    stickyAlertContext?.notifyError?.({
                        message
                    });
                    return 1;
                },
                addMany: () => undefined,
                addInfo: () => 0,
                addSuccess: message => {
                    stickyAlertContext?.notifySuccess?.({
                        message
                    });
                    return 1;
                },
                addWarning: () => 0,
                clear: () => undefined,
                clearAll: () => undefined
            }}
        >
            <Suspense fallback={<Loader />}>
                <LazyNav
                    navigateTo={handleNavigateTo}
                    graphqlEnabled={true}
                    graphqlProviderLoaded
                    theme={currentTheme}
                    onThemeToggle={toggleTheme}
                    menuMapper={(item, parentIds) => {
                        const modifiedFields = {};
                        if (hasParent(parentIds) && isMenuItemLink(item)) {
                            modifiedFields.Component = forwardRef((props, ref) => {
                                return (
                                    <Link {...props} to={props.href} ref={ref} />
                                );
                            });
                        }
                        return {
                            ...item,
                            ...modifiedFields
                        };
                    }}
                    unreadNotificationCount={unreadNotificationCount}
                    handleIntercom={startIntercom}
                    userPrivileges={kansoPrivileges}
                    onCollapseChange={bool => {
                        preferencesContext.updateState({
                            kansoIsCollapsed: bool
                        });
                    }}
                >
                    <div
                        className={`kanso-container ${
                            preferencesContext.kansoIsCollapsed
                                ? 'collapsed'
                                : 'expanded'
                        } ${
                            incidentSectionRE.test(pathname)
                                ? incidentSectionCls
                                : ''
                        }`}
                    >
                        {props.children}
                    </div>
                </LazyNav>
            </Suspense>
        </ToastsContext.Provider>
    );
};

const AppOldNav = props => {
    const location = useLocation();
    const pathname = location?.pathname;

    useEffect(() => {
        const htmlClasses = document.documentElement.classList;
        const hasIncidentSectionCls = htmlClasses.contains(incidentSectionCls);
        if (incidentSectionRE.test(pathname)) {
            if (!hasIncidentSectionCls) {
                htmlClasses.add(incidentSectionCls);
            }
        } else if (hasIncidentSectionCls) {
            htmlClasses.remove(incidentSectionCls);
        }
    }, [pathname]);

    return (
        <>
            {!props.isPrintPage && <Topbar />}
            <div className="oldui-container h-100">{props.children}</div>
        </>
    );
};

//----------------------------------------------------------------------------------------------------------------------# 𝘳𝘰𝘶𝘵𝘪𝘯𝘨

const checkSpecificPath = (url, section) => {
    return (
        url === returnSpecificPath(section, 'oldPath') ||
        url === returnSpecificPath(section, 'newUrlPath')
    );
};

const checkSpecificPathStartsWith = (url, section) => {
    return (
        url.startsWith(returnSpecificPath(section, 'oldPath')) ||
        url.startsWith(returnSpecificPath(section, 'newUrlPath'))
    );
};

//----------------------------------------------------------------------------------------------------------------------

// prettier-ignore
const PrivateRoute = ({
    component: Component,
    computedMatch,
    can = true,
    ...rest
}) => {
    // console.dir(rest);
    const [isReady, setIsReady] = useState(false);

    const canContext = useContext(CanContext);
    const preferencesContext = useContext(PreferencesContext);

    const { path, params, url: computedMatchUrl } = computedMatch;
    const paramsId = params.id;
    const isIncidentUrl = incidentSectionRE.test(computedMatchUrl);

    const ref = useRef({
        featureFlagFullCsoar: checkFeatureFlag('full_csoar'),
        featureFlagInstantMessaging:checkFeatureFlag('instant_messaging'),
        featureFlagAutomationService: checkFeatureFlag('automation_service')
    }).current;

    useEffect(() => {
        let sectionName = null;
        let preferencesTag = null;
        let pageIsincidentSection = false;
        const page =  params.page;
        if (isIncidentUrl) {
            for (const defaultValuesKey of defaultValuesKeys) {
                if (defaultValuesKey.startsWith('incident-')) {
                    const afterPrefix = defaultValuesKey.slice(9);
                    if (afterPrefix.includes(page)) {
                        // console.log('pageIsincidentSection', afterPrefix);
                        pageIsincidentSection = true;
                        break;
                    }
                }
            }
        }
        if (pageIsincidentSection) {
            sectionName = page;
            preferencesTag = `incident-${page}`;
            if ('playbook' === page) {
                sectionName = 'playbook';
                preferencesTag = 'incident-runbook'; // <<
            }
        } else {
            if ('/application_logs' === path) {
                preferencesTag = 'audit-trail';
                sectionName = 'audit trail';
            } else if (
                '/report-wizard|:id' === path ||
                '/report-wizard' === path
            ) {
                sectionName = 'report template';
                preferencesTag = 'report-template';
            } else {
                const pathIs = str => checkSpecificPath(computedMatchUrl, str);
                if (
                    pathIs('incidents')
                ) {
                    sectionName = 'incidents';
                    preferencesTag = 'incident-list';
                } else if (
                    pathIs('incidentsCreation')
                ) {
                    sectionName = null;
                    preferencesTag = 'incident-creation';
                } else if (
                    pathIs('triage')
                ) {
                    sectionName = 'triage';
                    preferencesTag = 'triage-list';
                } else if (
                    pathIs('bridges')
                ) {
                    sectionName = 'bridge';
                    preferencesTag = 'bridge-settings';
                } else if (
                    pathIs('playbookExecutions')
                ) {
                    sectionName = 'playbook executions';
                    preferencesTag = 'playbook-executions-list';
                } else {
                    const pathStartsWith = str => checkSpecificPathStartsWith(computedMatchUrl, str);
                    if (
                        pathStartsWith('integrations')
                    ) {
                        sectionName = 'integrations';
                        preferencesTag = 'integrations-list';
                    } else if (
                        pathStartsWith('groups')
                    ) {
                        sectionName = 'groups';
                        preferencesTag = 'groups-settings';
                    } else if (
                        pathStartsWith('templates')
                    ) {
                        sectionName = 'incident templates';
                        preferencesTag = 'incident-templates-settings';
                    } else if (
                        pathStartsWith('rules')
                    ) {
                        sectionName = 'automation rules';
                        preferencesTag = 'integration-rules-settings';
                    } else if (
                        pathStartsWith('fields')
                    ) {
                        sectionName = null;
                        preferencesTag = 'custom-fields';
                    } else if (
                        pathStartsWith('entities') ||
                        computedMatchUrl.startsWith('/artifacts')
                    ) {
                        sectionName = 'entities';
                        preferencesTag = 'entity-list';
                    } else if (
                        pathStartsWith('playbooks') ||
                        '/mssp-playbook|:id' === path ||
                        '/mssp-playbook' === path
                    ) {
                        sectionName = 'playbook';
                        preferencesTag = 'automation-runbook'; // <<
                    }
                }
            }
        }
        preferencesContext.loadUserPreferences(sectionName, preferencesTag);
    }, [computedMatchUrl]);

    useEffect(() => {
        const { currentIncidentId } = canContext;
        if (isIncidentUrl) {
            if (paramsId !== currentIncidentId) {
                canContext.refreshIncidentPermissions(paramsId, () => {
                    setIsReady(true);
                });
            }
        } else {
            if (currentIncidentId && 0 < parseInt(currentIncidentId)) {
                canContext.refreshIncidentPermissions(0);
            }
            if (!isReady) {
                setIsReady(true);
            }
        }
    }, [isIncidentUrl, paramsId]);

    if (isReady) {
        const {
            featureFlagFullCsoar,
            featureFlagInstantMessaging,
            featureFlagAutomationService
        } = ref;
        const checkPath = str => checkSpecificPath(computedMatchUrl, str);
        return (
            <Route
                {...rest}
                render={props => {
                    const access = {};
                    switch (typeof can) {
                        case 'string':
                        case 'object':
                            access.general = User.can(can);
                            break;
                        case 'function':
                            access.general = can();
                            break;
                        case 'boolean':
                            access.general = can;
                            break;
                    }
                    if (isIncidentUrl) {
                        const { currentIncidentPermissions } = canContext || {};
                        access.incident = Object.keys(currentIncidentPermissions).length
                            ? User.can(permissions.incident, currentIncidentPermissions)
                            : User.can('incident_manage_investigators');
                    }
                    const isSuperUser = 1 === CURRENT_USER.isSuperUser;
                    if ((
                            !access.general &&
                            !access.incident &&
                            !User.can(permissions.triage) &&
                            !(
                                !featureFlagFullCsoar &&
                                User.can('observability_access')
                            )
                        ) || (
                            checkPath('fields') && (
                                isSuperUser ||
                                !User.can('fields_management')
                            )
                        ) || (
                            checkPath('groups') && (
                                isSuperUser ||
                                !User.can('group_management')
                            )
                        ) || (
                            checkPath('entities') && (
                                isSuperUser ||
                                !User.can('objects_access')
                            )
                        ) || (
                            checkPath('playbooks') && (
                                isSuperUser ||
                                !User.can('playbook_access')
                            )
                        ) | (
                            checkPath('playbookExecutions') && (
                                isSuperUser ||
                                !User.can('observability_access')
                            )
                        ) || (
                            checkPath('rules') && (
                                isSuperUser ||
                                !User.can(permissions.automationRules)
                            )
                        ) || (
                            checkPath('integrations') && (
                                isSuperUser ||
                                !User.can(permissions.integrations)
                            )
                        ) || (
                            checkPath('templates') && (
                                isSuperUser ||
                                !User.can(permissions.incidentTemplate)
                            )
                        ) || (
                            checkPath('incidentLabels') && (
                                isSuperUser ||
                                !User.can('incident_labels_management')
                            )
                        ) || (
                            checkPath('triageSettings') && (
                                isSuperUser ||
                                !User.can('triage_management')
                            )
                        ) || (
                            '/automation-settings' === computedMatchUrl && (
                                isSuperUser ||
                                !access.general
                            )
                        ) || (
                            '/setting_logo' === computedMatchUrl && (
                                isSuperUser ||
                                !User.can('logo_management')
                            )
                        ) || (
                            '/application_logs' === computedMatchUrl && (
                                isSuperUser ||
                                !User.can('audit_trail_access')
                            )
                        ) || (
                            '/logrotation_management' === computedMatchUrl && (
                                isSuperUser ||
                                !User.can('audit_trail_management')
                            )
                        ) || (
                            (
                                '/license_settings' === computedMatchUrl ||
                                '/environment_status' === computedMatchUrl
                            ) && (
                                isSuperUser ||
                                !User.can('license_informations_management')
                            )
                        ) || (
                            (
                                '/setting_general' === computedMatchUrl ||
                                checkPath('generalSettings')
                            ) && (
                                !User.can('setting_general') || (
                                    featureFlagAutomationService &&
                                    !featureFlagInstantMessaging
                                )
                            )
                        )
                    ) {
                        return <AccessDenied />;
                    }
                    if (checkPath('incidents')) {
                        props = { ...props, preferencesContext };
                    }
                    return <Component {...props} {...rest} />;
                }}
            />
        );
    } else {
        return <Loader />;
    }
};

PrivateRoute.propTypes = {
    component: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.object,
        PropTypes.func
    ]),
    can: PropTypes.oneOfType([
        PropTypes.array, // 𝘛𝘩𝘦 𝘷𝘢𝘭𝘶𝘦 𝘸𝘪𝘭𝘭 𝘣𝘦 𝘱𝘢𝘴𝘴𝘦𝘥 𝘵𝘰 𝘜𝘴𝘦𝘳.𝘤𝘢𝘯() 𝘮𝘦𝘵𝘩𝘰𝘥 𝘸𝘪𝘵𝘩 "𝘪𝘮𝘴" 𝘥𝘰𝘮𝘢𝘪𝘯
        PropTypes.string, // 𝘛𝘩𝘦 𝘷𝘢𝘭𝘶𝘦 𝘸𝘪𝘭𝘭 𝘣𝘦 𝘱𝘢𝘴𝘴𝘦𝘥 𝘵𝘰 𝘜𝘴𝘦𝘳.𝘤𝘢𝘯() 𝘮𝘦𝘵𝘩𝘰𝘥 𝘸𝘪𝘵𝘩 "𝘪𝘮𝘴" 𝘥𝘰𝘮𝘢𝘪𝘯
        PropTypes.func, // 𝘸𝘪𝘭𝘭 𝘣𝘦 𝘦𝘹𝘦𝘤𝘶𝘵𝘦𝘥 𝘢𝘯𝘥 𝘵𝘩𝘦 𝘳𝘦𝘴𝘶𝘭𝘵 𝘸𝘪𝘭𝘭 𝘱𝘳𝘰𝘷𝘪𝘥𝘦 𝘵𝘩𝘦 𝘨𝘦𝘯𝘦𝘳𝘢𝘭 𝘢𝘤𝘤𝘦𝘴𝘴 𝘳𝘦𝘴𝘶𝘭𝘵 (𝗍𝗋𝗎𝖾 𝖼𝖺𝗇 𝖺𝖼𝖼𝖾𝗌𝗌)
        PropTypes.bool // 𝘥𝘪𝘳𝘦𝘤𝘵𝘭𝘺 𝘢𝘵𝘵𝘳𝘪𝘣𝘶𝘵𝘦𝘥 𝘵𝘰 𝘨𝘦𝘯𝘦𝘳𝘢𝘭 𝘢𝘤𝘤𝘦𝘴𝘴 𝘳𝘦𝘴𝘶𝘭𝘵
    ])
};

PrivateRoute.defaultProps = {
    can: true
};

//----------------------------------------------------------------------------------------------------------------------

const toNewPath = e => returnSpecificPath(e, 'newUrlPath');

// prettier-ignore
const browserRoutes = {
    incidentList: '/cases',
    incidentCreation: '/case-creation',
    reportPrint: [
        '/print/report/:id',
    ],
    dashboardPrint: [
        '/print/dashboard/:id',
    ],
    report: [
        '/reports/:id',
        '/reports'
    ],
    generalSettings: [
        '/cloud-soar-fields/:id',
        '/cloud-soar-fields',
        '/cloud-soar-groups/:id',
        '/cloud-soar-groups',
        '/cloud-soar-notifications',
        '/cloud-soar-settings',
        '/case-labels',
        '/triage-settings'
    ],
    automationSettings: [
        'appCentral',
        'appCentralExport',
        'bridges',
        'rulesDetails',
        'rules',
        'templatesDetails',
        'templates',
        'integrationsDetails',
        'integrations',
        'playbooksDetails',
        'playbooks',
        'playbookExecutions'
    ].map(
        toNewPath
    ),
    playbookExecuteDetails: [
        '/automation-service/:playbook_id',
        '/cse/:playbook_id'
    ],
    entities: [
        'entitiesDetails',
        'entities'
    ].map(
        toNewPath
    ),
    triageDetails: [
        '/triage/:id'
    ],
    incidentDetails: [
        '/cases/:id/:page/:entity/history/:executionId',
        '/cases/:id/:page/:entity',
        '/cases/:id/:page'
    ],
    dashboard: [
        '/cloud-soar-dashboard'
    ]
};

// prettier-ignore
const hashRoutes = {
    incidentList: '/ir',
    incidentCreation: '/incident-creation',
    reportPrint: [
        '/print|report|:id',
    ],
    dashboardPrint: [
        '/print|dashboard|:id',
    ],
    report: [
        '/report|:id',
        '/report'
    ],
    generalSettings: [
        '/license_settings',
        '/setting_label',
        '/setting_logo',
        '/setting_notification',
        '/setting_organizations',
        '/setting_incident_kind_customization',
        '/triage_setting',
        '/setting_triage',
        '/general-settings',
        '/setting_general|:section',
        '/setting_general',
        '/setting_user_interface|:id',
        '/setting_user_interface',
        '/playbook_template',
        '/playbook',
        '/report-wizard|:id',
        '/report-wizard',
        '/groups_management|:id',
        '/groups_management',
        '/label_management',
        '/employee_management',
        '/logrotation_management',
        '/application_logs'
    ],
    automationSettings: [
        '/automation-settings',
        '/app_central',
        '/app_central_export',
        '/3rd_party_integrations|:id',
        '/3rd_party_integrations',
        '/playbook|:id?|:action?',
        '/playbook|:id',
        '/playbook',
        '/template_wizard|:id',
        '/template_wizard',
        '/setting_mail_receiver',
        '/setting_integration_rules|:id',
        '/setting_integration_rules',
        '/bridge',
        '/playbook-executions'
    ],
    playbookExecuteDetails: [
        '/automation-service|:playbook_id',
        '/cse|:playbook_id'
    ],
    entities: [
        '/entities|:section|:id',
        '/entities',
        '/artifacts|:id'
    ],
    triageDetails: [
        '/triage|:id'
    ],
    incidentDetails: [
        '/incident|:id|:page|:entity|history|:executionId',
        '/incident|:id|:page|:entity',
        '/incident|:id|:page'
    ],
    support: [
        '/support_user|:section',
        '/support_integration|:section',
        '/support_apidoc|:section',
        '/support_apiguide|:section',
        '/support_automation_bridge|:section',
        '/support|:section',
        '/support'
    ],
    dashboard: [
        '/'
    ]
};

//----------------------------------------------------------------------------------------------------------------------

// prettier-ignore
const [
    RoutesRouter,
    uiRoutes,
    AppWrapper,
    RoutesWrapper
] = oldUI
        ? [
            HashRouter,
            hashRoutes,
            Fragment,
            AppOldNav
          ]
        : [
            BrowserRouter,
            browserRoutes,
            ApolloProvider,
            AppNewNav
        ];

const routes = {
    ...uiRoutes,
    triage: ['/triage'],
    userChoice: ['/userchoice|:id'], // todo
    playbookExecute: [
        '/automation-service-playbook-execute',
        '/cse-playbook-execute'
    ],
    training: ['/training']
};

// prettier-ignore
const AppContent = () => {

    const location = window.location; // note: useLocation() is not usable because still not ready

    const hash = location?.hash;
    const pathname = location?.pathname;
    const newuiFlagInUrl = location?.href?.indexOf('?newui') !== -1;

    // console.log({ hash, pathname, newuiFlagInUrl });

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const ref = useRef({
        featureFlagQe: checkFeatureFlag('qe'),
        featureFlagReport: checkFeatureFlag('report'),
        featureFlagTriage: checkFeatureFlag('triage'),
        featureFlagFullCsoar: checkFeatureFlag('full_csoar'),
        featureFlagTraining: checkFeatureFlag('training')
    }).current;

    const {
        featureFlagQe,
        featureFlagReport,
        featureFlagTriage,
        featureFlagFullCsoar,
        featureFlagTraining
    } = ref;

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * TEMPORARY for backward compatibility with old KANSO URLs
     *
     * If url have hash and newui param convert url to KANSO routing
     *  1 - remove hash
     *  2 - replace pipe '|' to slash '/'
     *  3 - change location with cleaned URL
     *
     * NOTE: if url match the base url (/csoar/ui/) redirect to dashboard page
     *
     */
    if (newuiFlagInUrl) {
        const baseUrl = '/csoar/ui/';
        if (hash) {
            window.location = `${baseUrl}${
                hash.substring(1)
                    .replaceAll('|', '/')
                    .replaceAll('%7C', '/')
            }`;
        } else {
            if (pathname === baseUrl) {
                window.location = `/cloud-soar-dashboard`;
            }
        }
    }
    

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const [isPrintPage, setIsPrintPage] = useState(false);

    useEffect(() => {
        const htmlClasses = document.documentElement.classList;
        const printPageCls = 'print-page';
        htmlClasses.remove(printPageCls);
        const urlToCheck = oldUI ? hash : pathname;
        const isMatchingPrintPattern = printPageUrlRE.test(urlToCheck);
        if (isMatchingPrintPattern) {
            htmlClasses.add(printPageCls);
        }
        setIsPrintPage(isMatchingPrintPattern);
    }, [hash]);

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    
    return (
        <PreferencesManager>
            <ReactQueryProvider>
                <AppWrapper>
                    <RoutesWrapper isPrintPage={isPrintPage}>
                        <Suspense fallback={<Loader />}>
                            <Switch>
                                <Redirect
                                    from="/csoar/ui/incident/:id/:page/:entity/history/:executionId"
                                    to={{
                                        pathname: '/cases/:id/:page/:entity/history/:executionId'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/incident/:id/:page/:entity"
                                    to={{
                                        pathname: '/cases/:id/:page/:entity'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/incident/:id/:page"
                                    to={{
                                        pathname: '/cases/:id/:page'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/ir"
                                    to={{
                                        pathname: '/cases'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/incident-creation"
                                    to={{
                                        pathname: '/case-creation'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/groups_management/:id"
                                    to={{
                                        pathname: '/cloud-soar-groups/:id'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/groups_management"
                                    to={{
                                        pathname: '/cloud-soar-groups'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_user_interface/:id"
                                    to={{
                                        pathname: '/cloud-soar-fields/:id'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_user_interface"
                                    to={{
                                        pathname: '/cloud-soar-fields'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/triage/:id"
                                    to={{
                                        pathname: '/triage/:id'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/triage"
                                    to={{
                                        pathname: '/triage'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_notification"
                                    to={{
                                        pathname: '/cloud-soar-notifications'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_triage"
                                    to={{
                                        pathname: '/triage-settings'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_label"
                                    to={{
                                        pathname: '/case-labels'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_general"
                                    to={{
                                        pathname: '/cloud-soar-settings'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/automation-service/:playbook_id"
                                    to={{
                                        pathname: '/automation-service/:playbook_id'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/automation-service-playbook-execute"
                                    to={{
                                        pathname: '/automation-service-playbook-execute'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/cse/:playbook_id"
                                    to={{
                                        pathname: '/automation-service/:playbook_id'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/cse-playbook-execute"
                                    to={{
                                        pathname: '/automation-service-playbook-execute'
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/bridge"
                                    to={{
                                        pathname: toNewPath('bridges')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/app_central_export"
                                    to={{
                                        pathname: toNewPath('appCentralExport')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/app_central"
                                    to={{
                                        pathname: toNewPath('appCentral')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/entities|:id"
                                    to={{
                                        pathname: toNewPath('entitiesDetails')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/entities"
                                    to={{
                                        pathname: toNewPath('entities')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/playbook/:id"
                                    to={{
                                        pathname: toNewPath('playbooksDetails')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/playbook"
                                    to={{
                                        pathname: toNewPath('playbooks')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/playbook-executions"
                                    to={{
                                        pathname: toNewPath('playbookExecutions')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/3rd_party_integrations|:id"
                                    to={{
                                        pathname: toNewPath('integrationsDetails')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/3rd_party_integrations"
                                    to={{
                                        pathname: toNewPath('integrations')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/template_wizard|:id"
                                    to={{
                                        pathname: toNewPath('templatesDetails')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/template_wizard"
                                    to={{
                                        pathname: toNewPath('templates')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_integration_rules|:id"
                                    to={{
                                        pathname: toNewPath('rulesDetails')
                                    }}
                                />
                                <Redirect
                                    from="/csoar/ui/setting_integration_rules"
                                    to={{
                                        pathname: toNewPath('rules')
                                    }}
                                />
                                {featureFlagReport && (
                                    <Redirect
                                        from="/csoar/ui/report/:id"
                                        to={{
                                            pathname: '/reports/:id'
                                        }}
                                    />
                                )}
                                {featureFlagReport && (
                                    <Redirect
                                        from="/csoar/ui/report"
                                        to={{
                                            pathname: '/reports'
                                        }}
                                    />
                                )}
                                {featureFlagReport && (
                                    <Redirect
                                        from="/csoar/ui/print/report/:id"
                                        to={{
                                            pathname: '/print/report/:id'
                                        }}
                                    />
                                )}
                                {featureFlagReport && (
                                    <Redirect
                                        from="/csoar/ui/print/dashboard/:id"
                                        to={{
                                            pathname: '/print/dashboard/:id'
                                        }}
                                    />
                                )}
                                {featureFlagTraining && CURRENT_USER.training && (
                                    <Redirect
                                        from="/csoar/ui/training"
                                        to={{
                                            pathname: '/training'
                                        }}
                                    />
                                )}
                                <Redirect
                                    from="/csoar/ui/home"
                                    to={{
                                        pathname: '/cloud-soar-dashboard'
                                    }}
                                />
                                {featureFlagReport && (
                                    <PrivateRoute
                                        component={ReportRendering}
                                        path={routes.reportPrint}
                                    />
                                )}
                                {featureFlagReport && (
                                    <PrivateRoute
                                        component={DashboardRendering}
                                        path={routes.dashboardPrint}
                                    />
                                )}
                                {featureFlagReport && (
                                    <PrivateRoute
                                        component={Report}
                                        path={routes.report}
                                        can={permissions.report}
                                    />
                                )}
                                {featureFlagTriage && (
                                    <PrivateRoute
                                        component={TriageDetails}
                                        path={routes.triageDetails}
                                        can={permissions.triage}
                                    />
                                )}
                                {featureFlagTriage && (
                                    <PrivateRoute
                                        component={TriageListServer}
                                        path={routes.triage}
                                        can={permissions.triage}
                                    />
                                )}
                                {featureFlagTraining && CURRENT_USER.training && (
                                    <PrivateRoute
                                        component={Training}
                                        path={routes.training}
                                    />
                                )}
                                {featureFlagQe && (
                                    <PrivateRoute
                                        component={PlaybookExecute}
                                        path={routes.playbookExecute}
                                        can={permissions.playbookExecutes}
                                    />
                                )}
                                <PrivateRoute
                                    component={PlaybookExecuteDetailsPage}
                                    path={routes.playbookExecuteDetails}
                                    can={permissions.playbookExecutes}
                                />
                                {oldUI && (
                                    <PrivateRoute
                                        component={Support}
                                        path={routes.support}
                                    />
                                )}
                                <PrivateRoute
                                    component={DashboardOrHome}
                                    path={routes.dashboard}
                                    exact
                                />
                                <PrivateRoute
                                    component={IncidentCreation}
                                    path={routes.incidentCreation}
                                    can={permissions.incidentCreation}
                                />
                                <PrivateRoute
                                    component={IncidentDetails}
                                    path={routes.incidentDetails}
                                    can={permissions.incident}
                                />
                                <PrivateRoute
                                    component={IncidentList}
                                    path={routes.incidentList}
                                    can={permissions.incident}
                                />
                                <PrivateRoute
                                    component={EntitiesServer}
                                    path={routes.entities}
                                    can={permissions.entities}
                                />
                                <PrivateRoute
                                    component={UserChoice}
                                    path={routes.userChoice}
                                />
                                <PrivateRoute
                                    component={AutomationSettings}
                                    path={routes.automationSettings}
                                    can={permissions.automationSettings}
                                />
                                <PrivateRoute
                                    component={GeneralSettings}
                                    path={routes.generalSettings}
                                />
                                <PrivateRoute
                                    component={UserPreferenceReset}
                                    path="/reset-user-preference"
                                />
                                <PrivateRoute component={Sandbox} path="/sandbox" />
                                <PrivateRoute
                                    component={PageNotFound}
                                    path="/404"
                                />
                                <PrivateRoute component={PageNotFound} />
                            </Switch>
                        </Suspense>
                        {featureFlagFullCsoar && <ContextualMenu />}
                    </RoutesWrapper>
                </AppWrapper>
            </ReactQueryProvider>
        </PreferencesManager>
    );
};

export const AppRouter = () => {
    return (
        <RoutesRouter hashType="noslash">
            <AppContent />
        </RoutesRouter>
    );
};
